/**
 * BlacklistSection: the organisation's do-not-call list (RGPD), by number
 * (W4, B2-settings-blacklist; audit B.2 « Blacklist RGPD »).
 *
 *   - one row per blocked number: the contacts carrying it in every list,
 *     when, by whom (an admin, or « Appel » for a block from a call), why;
 *   - « Ajouter un numéro »: blocks the number in every list, whatever its
 *     spelling, and keeps it for the contacts imported later;
 *   - search (number or name), CSV export, journal (who / when / why);
 *   - « Retirer » is destructive for RGPD (the contacts become callable
 *     again): ConfirmDialog with an optional reason (DES-13), errors in a
 *     toast with the code (DES-16), the row stays on failure.
 *
 * Server: /api/blacklist (server/src/adapters/inbound/http/blacklist.routes.ts),
 * admins only; codes in docs/api/errors.md « Liste noire ».
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { toCsv, downloadCsv } from '@/lib/exportCsv';
import { errorCodeOf, errorMessage } from '@/services/errorMessage';
import { addToBlacklist, blacklistJournal, listBlacklist, removeFromBlacklist, type BlacklistEntry } from '@/api/endpoints/blacklist';
import { SettingsCard } from './SettingsPrimitives';

const QUERY_KEY = 'blacklist';
const fmtDate = (iso: string | null, lang: string) => (iso ? new Date(iso).toLocaleString(lang, { dateStyle: 'short', timeStyle: 'short' }) : '—');
const rowId = (e: BlacklistEntry) => e.phone_key || `contact:${e.contacts[0]?.id ?? ''}`;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

function reportError(err: unknown, t: (k: string, o?: Record<string, unknown>) => string, fallbackKey: string) {
  const code = errorCodeOf(err);
  toast.error(errorMessage(err, t, fallbackKey), code ? { description: code } : undefined);
}

export function BlacklistSection(_props: { d?: unknown } = {}) {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { isTenantAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 300);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<BlacklistEntry | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [journalOpen, setJournalOpen] = useState(false);

  const list = useQuery({
    queryKey: [QUERY_KEY, q],
    queryFn: ({ signal }) => listBlacklist({ q, limit: 1000 }, signal),
    enabled: isTenantAdmin,
  });
  const journal = useQuery({
    queryKey: [QUERY_KEY, 'journal'],
    queryFn: ({ signal }) => blacklistJournal(signal),
    enabled: isTenantAdmin && journalOpen,
  });

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
  };

  if (!isTenantAdmin) {
    return <p className="text-sm text-muted-foreground">{t('settings:blacklist.askAdmin')}</p>;
  }

  const exportCsv = () => {
    if (!items.length) return;
    const headers = [
      t('settings:blacklist.csv.phone'),
      t('settings:blacklist.csv.name'),
      t('settings:blacklist.csv.blockedAt'),
      t('settings:blacklist.csv.blockedBy'),
      t('settings:blacklist.csv.reason'),
    ];
    const rows = items.map((e) => [
      e.phone,
      e.contacts.map((c) => c.name).filter(Boolean).join(' / '),
      e.blocked_at ?? '',
      e.blocked_by ?? (e.manual ? '' : t('settings:blacklist.fromCall')),
      e.reason,
    ]);
    downloadCsv(`blacklist-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, rows));
  };

  const confirmRemove = async () => {
    if (!removing) return;
    const body = removing.phone
      ? { phone: removing.phone, reason: removeReason.trim() || undefined }
      : { contact_id: removing.contacts[0]?.id, reason: removeReason.trim() || undefined };
    try {
      const res = await removeFromBlacklist(body);
      toast.success(t('settings:blacklist.removedCount', { count: res.unblocked_contacts }));
      setRemoving(null);
      setRemoveReason('');
      refresh();
    } catch (err) {
      reportError(err, t, 'settings:blacklist.removeFailed');
      throw err; // keeps the dialog open for a retry (ConfirmDialog)
    }
  };

  const columns: DataTableColumn<BlacklistEntry>[] = [
    {
      id: 'phone',
      header: t('settings:blacklist.columns.phone'),
      cell: (e) => <span className="font-mono text-[13px]">{e.phone || '—'}</span>,
    },
    {
      id: 'contacts',
      header: t('settings:blacklist.columns.contacts'),
      cell: (e) => (e.contacts.length === 0
        ? <span className="text-muted-foreground">{t('settings:blacklist.noContactYet')}</span>
        : (
          <span className="block max-w-[280px] truncate" title={e.contacts.map((c) => [c.name, c.company].filter(Boolean).join(' · ')).join('\n')}>
            {e.contacts[0]?.name || '—'}
            {e.contacts.length > 1 ? ` ${t('settings:blacklist.andOthers', { count: e.contacts.length - 1 })}` : ''}
          </span>
        )),
    },
    { id: 'blockedAt', header: t('settings:blacklist.columns.blockedAt'), cell: (e) => fmtDate(e.blocked_at, i18n.language) },
    {
      id: 'by',
      header: t('settings:blacklist.columns.by'),
      cell: (e) => e.blocked_by ?? (e.manual ? '—' : t('settings:blacklist.fromCall')),
      hideable: true,
    },
    {
      id: 'reason',
      header: t('settings:blacklist.columns.reason'),
      cell: (e) => <span className="block max-w-[220px] truncate" title={e.reason}>{e.reason || '—'}</span>,
      hideable: true,
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('settings:blacklist.columns.actions')}</span>,
      align: 'right',
      cell: (e) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-11 text-xs text-destructive"
          aria-label={t('settings:blacklist.removeAria', { phone: e.phone || e.contacts[0]?.name || '' })}
          onClick={() => { setRemoveReason(''); setRemoving(e); }}
        >
          {t('settings:blacklist.remove')}
        </Button>
      ),
    },
  ];

  const total = list.data?.total ?? 0;

  return (
    <div className="space-y-5">
      <SettingsCard>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <span className="text-sm font-medium text-foreground" aria-live="polite">
            {list.isSuccess ? t('settings:blacklist.blockedNumbers', { count: total }) : ' '}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                type="search"
                value={search}
                onChange={(ev) => setSearch(ev.target.value)}
                placeholder={t('settings:blacklist.searchPlaceholder')}
                aria-label={t('settings:blacklist.searchPlaceholder')}
                className="h-9 w-56 pl-8"
              />
            </div>
            <Button variant="outline" size="sm" className="h-9" onClick={exportCsv} disabled={!items.length}>
              {t('settings:blacklist.exportCsv')}
            </Button>
            <Button size="sm" className="h-9" onClick={() => setAdding(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {t('settings:blacklist.add')}
            </Button>
          </div>
        </div>
        <div className="p-3">
          <DataTable
            caption={t('settings:blacklist.title')}
            columns={columns}
            rows={items}
            getRowId={rowId}
            status={list.isLoading ? 'loading' : list.isError ? 'error' : 'ready'}
            error={list.isError ? { message: t('settings:blacklist.loadFailed'), code: errorCodeOf(list.error) || undefined, onRetry: () => void list.refetch() } : undefined}
            filters={q ? [{ id: 'q', label: t('settings:blacklist.searchPill', { q }), onRemove: () => setSearch('') }] : []}
            onClearFilters={() => setSearch('')}
            empty={{
              title: t('settings:blacklist.emptyTitle'),
              action: <Button size="sm" onClick={() => setAdding(true)}>{t('settings:blacklist.add')}</Button>,
            }}
            partial={list.data?.truncated ? { shown: items.length, total, reason: t('settings:blacklist.truncated') } : undefined}
            storageKey="settings-blacklist"
          />
        </div>
      </SettingsCard>

      <SettingsCard>
        <button
          type="button"
          className="flex min-h-11 w-full items-center justify-between px-5 py-3 text-left text-sm font-medium text-foreground"
          aria-expanded={journalOpen}
          onClick={() => setJournalOpen((o) => !o)}
        >
          {t('settings:blacklist.journal.title')}
          <span className="text-xs text-muted-foreground">{journalOpen ? t('settings:blacklist.journal.hide') : t('settings:blacklist.journal.show')}</span>
        </button>
        {journalOpen && (
          <div className="border-t border-border px-5 py-3 text-sm">
            {journal.isLoading ? (
              <p className="text-muted-foreground">{t('settings:blacklist.journal.loading')}</p>
            ) : journal.isError ? (
              <div className="flex items-center gap-3">
                <p>{t('settings:blacklist.journal.failed')}</p>
                <Button variant="outline" size="sm" onClick={() => void journal.refetch()}>{t('common:actions.retry')}</Button>
              </div>
            ) : (journal.data?.items.length ?? 0) === 0 ? (
              <p className="text-muted-foreground">{t('settings:blacklist.journal.empty')}</p>
            ) : (
              <ul className="divide-y divide-border" data-testid="blacklist-journal">
                {journal.data!.items.map((j, i) => (
                  <li key={`${j.created_date}-${i}`} className="py-2">
                    <span className="text-foreground">
                      {t(j.action === 'blacklist.add' ? 'settings:blacklist.journal.added' : 'settings:blacklist.journal.removed', { who: j.actor_email || '?', phone: j.phone || '—', count: j.contacts })}
                    </span>
                    <span className="ml-2 text-xs text-muted-foreground">{fmtDate(j.created_date, i18n.language)}</span>
                    {j.reason && <p className="text-xs text-muted-foreground">{t('settings:blacklist.journal.reason', { reason: j.reason })}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </SettingsCard>

      <AddNumberDialog open={adding} onOpenChange={setAdding} onAdded={refresh} />

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(open) => { if (!open) setRemoving(null); }}
        title={t('settings:blacklist.removeTitle', { phone: removing?.phone || removing?.contacts[0]?.name || '' })}
        description={(
          <>
            <span className="block">{t('settings:blacklist.removeWhy', { count: removing?.contacts.length ?? 0 })}</span>
            <label className="mt-3 block space-y-1 text-left text-foreground">
              <span className="block text-xs">{t('settings:blacklist.reasonLabel')}</span>
              <Input value={removeReason} maxLength={500} onChange={(ev) => setRemoveReason(ev.target.value)} />
            </label>
          </>
        )}
        confirmLabel={t('settings:blacklist.removeConfirm')}
        onConfirm={confirmRemove}
      />
    </div>
  );
}

/** « Ajouter un numéro »: the number and why. Blocking is reversible from this screen, so no confirmation. */
function AddNumberDialog({ open, onOpenChange, onAdded }: { open: boolean; onOpenChange: (open: boolean) => void; onAdded: () => void }) {
  const { t } = useTranslation(['settings', 'common']);
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const close = () => {
    if (busy) return;
    setPhone('');
    setReason('');
    onOpenChange(false);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const res = await addToBlacklist({ phone: phone.trim(), reason: reason.trim() || undefined });
      toast.success(t('settings:blacklist.addedCount', { count: res.blocked_contacts, phone: res.phone }));
      onAdded();
      setBusy(false);
      setPhone('');
      setReason('');
      onOpenChange(false);
    } catch (err) {
      reportError(err, t, 'settings:blacklist.addFailed');
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v: boolean) => { if (!v) close(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t('settings:blacklist.addTitle')}</DialogTitle>
          <DialogDescription>{t('settings:blacklist.addWhy')}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(ev) => { ev.preventDefault(); if (phone.trim() && !busy) void submit(); }}
        >
          <div>
            <Label htmlFor="blacklist-phone" className="text-xs">{t('settings:blacklist.phoneLabel')}</Label>
            <Input id="blacklist-phone" className="mt-1 h-9" type="tel" inputMode="tel" autoComplete="off" value={phone} maxLength={40} placeholder="+33 6 12 34 56 78" onChange={(ev) => setPhone(ev.target.value)} />
          </div>
          <div>
            <Label htmlFor="blacklist-reason" className="text-xs">{t('settings:blacklist.reasonLabel')}</Label>
            <Input id="blacklist-reason" className="mt-1 h-9" value={reason} maxLength={500} placeholder={t('settings:blacklist.reasonPlaceholder')} onChange={(ev) => setReason(ev.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" className="h-9" disabled={busy} onClick={close}>{t('common:actions.cancel')}</Button>
            <Button type="submit" size="sm" className="h-9" disabled={!phone.trim() || busy}>{t('settings:blacklist.addSubmit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
